import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Grid, Avatar, IconButton, Divider,
  CircularProgress, Alert,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PremiumDatePicker from "../../Components/PremiumDatePicker";
import { updateLead, type LeadThread } from "./leadApi";

const T = {
  navy: "#1E4D8F", gold: "#D4AF37",
  white: "#fff", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
} as const;

const EVENT_TYPES = [
  { value: "wedding",      label: "Wedding" },
  { value: "corporate",    label: "Corporate" },
  { value: "birthday",     label: "Birthday" },
  { value: "engagement",   label: "Engagement" },
  { value: "mehndi",       label: "Mehndi" },
  { value: "charity_gala", label: "Charity Gala" },
  { value: "convocation",  label: "Convocation" },
  { value: "baby_shower",  label: "Baby Shower" },
  { value: "anniversary",  label: "Anniversary" },
  { value: "retirement",   label: "Retirement" },
  { value: "holiday_party",label: "Holiday Party" },
  { value: "other",        label: "Other" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{10}$/;

const stripNonDigits = (p: string) => p.replace(/\D/g, "");

function validatePhone(p: string): string | undefined {
  if (!p) return undefined;
  if (p.length !== 10 || !PHONE_RE.test(p)) return "Phone must be exactly 10 digits";
  return undefined;
}

function validateEmail(e: string): string | undefined {
  if (!e) return undefined;
  if (!EMAIL_RE.test(e)) return "Enter a valid email address";
  return undefined;
}

interface FormState {
  name: string; email: string; phone: string; organization: string;
  event_type: string; event_date: string; guest_count: string;
  budget_value: string; preferred_hall: string;
}

interface FieldErrors { name?: string; email?: string; phone?: string }

function leadToForm(lead: LeadThread): FormState {
  return {
    name:           lead.name ?? "",
    email:          lead.email ?? "",
    phone:          lead.phone ?? "",
    organization:   lead.organization ?? "",
    event_type:     lead.event_type ?? "",
    event_date:     lead.event_date ?? "",
    guest_count:    lead.guest_count != null ? String(lead.guest_count) : "",
    budget_value:   lead.budget_value != null ? String(lead.budget_value) : "",
    preferred_hall: lead.preferred_hall ?? "",
  };
}

interface Props {
  open: boolean;
  lead: LeadThread;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditLeadDialog({ open, lead, onClose, onSaved }: Props) {
  const initial = useMemo(() => leadToForm(lead), [lead]);
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setErrors({});
      setSaveError(null);
    }
  }, [open, initial]);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let val = e.target.value;
    if (field === "phone") {
      val = stripNonDigits(val).slice(0, 10);
      setForm((p) => ({ ...p, phone: val }));
      setErrors((p) => ({ ...p, phone: validatePhone(val) }));
      return;
    }
    if (field === "email") {
      val = val.trimStart().toLowerCase();
      setForm((p) => ({ ...p, email: val }));
      setErrors((p) => ({ ...p, email: validateEmail(val.trim()) }));
      return;
    }
    setForm((p) => ({ ...p, [field]: val }));
  };

  // Diff form against initial — only changed fields get sent so the
  // activity log records the exact subset the operator touched.
  const dirty = useMemo(() => {
    const out: Record<string, any> = {};
    if (form.name.trim() !== initial.name)                                   out.name = form.name.trim();
    if (form.email.trim() !== initial.email)                                 out.email = form.email.trim() || null;
    if (form.phone !== initial.phone)                                        out.phone = form.phone || null;
    if (form.organization !== initial.organization)                          out.organization = form.organization || null;
    if (form.event_type !== initial.event_type)                              out.event_type = form.event_type || null;
    if (form.event_date !== initial.event_date)                              out.event_date = form.event_date || null;
    if (form.guest_count !== initial.guest_count) {
      out.guest_count = form.guest_count ? parseInt(form.guest_count, 10) : null;
    }
    if (form.budget_value !== initial.budget_value) {
      const v = form.budget_value ? parseFloat(form.budget_value) : null;
      out.budget_value = v;
      out.budget_known = v != null && v > 0;
    }
    if (form.preferred_hall !== initial.preferred_hall)                      out.preferred_hall = form.preferred_hall || null;
    return out;
  }, [form, initial]);

  const hasChanges = Object.keys(dirty).length > 0;
  const hasErrors = Object.values(errors).some(Boolean);
  const nameInvalid = !form.name.trim();

  const handleSubmit = async () => {
    if (nameInvalid || hasErrors || !hasChanges) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateLead(lead.id, dirty);
      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.message ||
        (err?.errors ? Object.values(err.errors).flat().join(" ") : null) ||
        "Failed to save changes. Please try again.";
      setSaveError(msg);
    }
    setSaving(false);
  };

  const nameInitial = form.name.trim() ? form.name.trim()[0].toUpperCase() : "?";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: 13.5, borderRadius: "6px",
      "& fieldset": { borderColor: "#dde1e8" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: T.navy, borderWidth: 1.5 },
    },
    "& .MuiInputLabel-root": { fontSize: 13 },
    "& .MuiFormHelperText-root": { fontSize: 11, mt: "3px" },
  };

  const sectionLabel = (text: string) => (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: T.slateLt,
      textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5,
    }}>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(13,27,42,0.2)",
        },
      }}
    >
      <Box sx={{
        background: `linear-gradient(135deg, ${T.navy} 0%, #1a3354 100%)`,
        px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
      }}>
        <Avatar sx={{
          width: 44, height: 44, bgcolor: T.gold,
          fontSize: 18, fontWeight: 700, color: T.navy,
          boxShadow: "0 4px 12px rgba(201,169,110,0.35)",
        }}>
          {nameInitial}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>
            Edit Lead
          </Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)", mt: 0.2 }}>
            {lead.lead_number ? `${lead.lead_number} · ` : ""}Changes are logged to this lead's history
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}
          sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: T.white, bgcolor: "rgba(255,255,255,0.08)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        {saveError && (
          <Box sx={{ px: 3, pt: 2.5 }}>
            <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.78rem", py: 0.75 }}>
              {saveError}
            </Alert>
          </Box>
        )}

        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {sectionLabel("Contact Information")}

          <TextField
            label="Full Name *"
            size="small"
            fullWidth
            value={form.name}
            onChange={set("name")}
            error={nameInvalid}
            helperText={nameInvalid ? "Name is required" : undefined}
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Email Address"
                size="small"
                fullWidth
                value={form.email}
                onChange={set("email")}
                error={!!errors.email}
                helperText={errors.email}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone Number"
                size="small"
                fullWidth
                value={form.phone}
                onChange={set("phone")}
                error={!!errors.phone}
                helperText={errors.phone || (form.phone ? `${form.phone.length} digits` : undefined)}
                inputProps={{ inputMode: "numeric" }}
                sx={fieldSx}
              />
            </Grid>
          </Grid>

          <TextField
            label="Organization"
            size="small"
            fullWidth
            value={form.organization}
            onChange={set("organization")}
            sx={{ ...fieldSx, mt: 2 }}
          />
        </Box>

        <Divider sx={{ borderColor: T.line }} />

        <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
          {sectionLabel("Event Details")}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel sx={{ fontSize: 13 }}>Event Type</InputLabel>
                <Select
                  value={form.event_type}
                  label="Event Type"
                  onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}
                  sx={{ fontSize: 13.5 }}
                >
                  <MenuItem value="" sx={{ fontSize: 13.5, color: T.slateLt }}>
                    <em>None</em>
                  </MenuItem>
                  {EVENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13.5 }}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <PremiumDatePicker
                label="Event Date"
                value={form.event_date}
                onChange={(val) => setForm((p) => ({ ...p, event_date: val }))}
                size="small"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Guest Count"
                type="number"
                size="small"
                fullWidth
                value={form.guest_count}
                onChange={set("guest_count")}
                inputProps={{ min: 1 }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Budget ($)"
                type="number"
                size="small"
                fullWidth
                value={form.budget_value}
                onChange={set("budget_value")}
                inputProps={{ min: 0 }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Preferred Hall"
                size="small"
                fullWidth
                value={form.preferred_hall}
                onChange={set("preferred_hall")}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        bgcolor: "#f8f9fb",
        borderTop: `1px solid ${T.line}`,
        gap: 1,
        justifyContent: "space-between",
      }}>
        <Typography sx={{ fontSize: 11.5, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.6 }}>
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
          {hasChanges
            ? `${Object.keys(dirty).length} field${Object.keys(dirty).length === 1 ? "" : "s"} changed`
            : "No changes"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              color: T.slate, px: 2.5, borderRadius: "6px",
              "&:hover": { bgcolor: "#eef0f4" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || nameInvalid || hasErrors || !hasChanges}
            startIcon={saving
              ? <CircularProgress size={15} sx={{ color: T.white }} />
              : <SaveOutlinedIcon sx={{ fontSize: 17 }} />
            }
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 13,
              px: 3, borderRadius: "6px",
              bgcolor: T.navy,
              boxShadow: "0 2px 8px rgba(13,27,42,0.2)",
              "&:hover": { bgcolor: "#1a3354", boxShadow: "0 4px 14px rgba(13,27,42,0.3)" },
              "&.Mui-disabled": { bgcolor: "#c5cdd8", color: T.white },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
